import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import {
  ArrowLeftRight,
  BarChart3,
  Package,
  ShieldOff,
  TrendingUp,
  UserCheck,
  Users,
  Warehouse,
  Wifi,
} from "lucide-solid";
import type { LucideProps } from "lucide-solid";
import type { Component } from "solid-js";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@stockpilot/shared";
import { useI18n } from "@/providers/i18n-provider";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminMetrics = components["schemas"]["AdminMetrics"];

interface Stat {
  label: string;
  value: number;
  icon: Component<LucideProps>;
}

export function MetricsDashboard() {
  const { t } = useI18n();

  const query = createApiQuery<AdminMetrics, ErrorModel>(
    () => adminQueryKeys.metrics(),
    async () => api.GET("/api/admin/metrics", {}),
    () => ({ staleTime: 30_000 }),
  );

  const stats = (): Stat[] => {
    const m = query.data;
    if (!m) {
      return [];
    }
    return [
      { label: t(msg`Total users`), value: m.totalUsers, icon: Users },
      { label: t(msg`Verified users`), value: m.verifiedUsers, icon: UserCheck },
      { label: t(msg`Banned users`), value: m.bannedUsers, icon: ShieldOff },
      { label: t(msg`Active sessions`), value: m.activeSessions, icon: Wifi },
      { label: t(msg`New signups (7d)`), value: m.newSignups7d, icon: TrendingUp },
      { label: t(msg`New signups (30d)`), value: m.newSignups30d, icon: TrendingUp },
      { label: t(msg`Total products`), value: m.totalProducts, icon: Package },
      { label: t(msg`Total warehouses`), value: m.totalWarehouses, icon: Warehouse },
      { label: t(msg`Total stock moves`), value: m.totalStockMoves, icon: ArrowLeftRight },
    ];
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <BarChart3 class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Metrics`)}</h2>
          <p class="text-sm text-muted-foreground">{t(msg`At-a-glance system health.`)}</p>
        </div>
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <For each={Array.from({ length: 9 }, () => 0)}>
              {() => <Skeleton class="h-28 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <For each={stats()}>
            {(stat) => (
              <Card>
                <CardContent class="p-5 space-y-2">
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-muted-foreground">{stat.label}</span>
                    <stat.icon class="h-4 w-4 text-primary" />
                  </div>
                  <div class="text-3xl font-bold tracking-tight">{stat.value.toLocaleString()}</div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
